import React, { useState } from 'react';
import { motion } from 'framer-motion';
import BackButton from '../components/BackButton';

const AboutPage = () => {
  const [activeCardId, setActiveCardId] = useState('professional');
  const [isFlipping, setIsFlipping] = useState(false);

  // Card data structure
  const cards = [
    {
      id: 'professional',
      gradient: 'linear-gradient(135deg, #F6D365 0%, #FDA085 100%)',
      accentColor: '#FDA085',
      type: 'WORKPLACE',
      title: 'PROFESSIONAL AKI',
      powerTitle: 'PRODUCT MANAGER',
      powers: ['Roadmap Telepathy', 'Scope Shield', 'Deadline Aura']
    },
    {
      id: 'figma',
      gradient: 'linear-gradient(135deg, #A18CD1 0%, #FBC2EB 100%)',
      accentColor: '#A18CD1',
      type: 'SIDE QUEST',
      title: 'FIGMA AKI',
      powerTitle: 'DESIGN',
      powers: ['Mockup warrior', 'Consistency fanatic','Viewpoint stalker']
    },
    {
      id: 'llm',
      gradient: 'linear-gradient(135deg, #C471F5 0%, #FA71CD 100%)',
      accentColor: '#C471F5',
      type: 'WORKPLACE',
      title: 'LLM POWERED AKI',
      powerTitle: 'VIBE CODER',
      powers: ['Cursor Summoning Ritual', 'Git Push & Pray', 'Vibe Spec Manifestation']
    },
    {
      id: 'sporty',
      gradient: 'linear-gradient(135deg, #74EBD5 0%, #9FACE6 100%)',
      accentColor: '#9FACE6',
      type: 'HOBBIES',
      title: 'SPORTY AKI',
      powerTitle: 'RUNNER',
      powers: ['Second Wind Dash', 'Blister de la Resistance', 'Pain Cave Mode']
    }
  ];

  const activeCard = cards.find(card => card.id === activeCardId);

  // Map card IDs to their corresponding images
  const getCharacterImage = (cardId) => {
    const imageMap = {
      'professional': '/profile.png',
      'figma': '/colors.jpg',
      'sporty': '/running.jpeg',
      'llm': '/github.jpeg'
    };
    return imageMap[cardId] || '/profile.png';
  };

  // Map card IDs to their selector text
  const getSelectorText = (cardId) => {
    const textMap = {
      'professional': 'product',
      'figma': 'design',
      'sporty': 'sports',
      'llm': 'code'
    };
    return textMap[cardId] || 'AKI KIM';
  };

  const handleCardSwitch = (newCardId) => {
    if (isFlipping || newCardId === activeCardId) return;
    
    setIsFlipping(true);
    
    setTimeout(() => {
      setActiveCardId(newCardId);
      setIsFlipping(false);
    }, 300);
  };

  return (
    <motion.div
      className="min-h-screen bg-page-bg flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header with back button */}
      <div className="w-full px-4 pt-4 lg:px-20">
        <BackButton color="rgba(0,0,0,.7)" hoverColor="#000" fontFamily="'K2D', sans-serif" />
      </div>

      {/* Main content - vertically centered */}
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            
            {/* Left Section - Character Cards (3 columns on desktop) */}
            <div className="lg:col-span-4">
              <div className="flex flex-col items-center lg:items-start">
                
                {/* Active Character Card */}
                <div className="w-full mb-5">
                  <motion.div
                    key={activeCardId}
                    initial={{ rotateY: -90, opacity: 0.8 }}
                    animate={{ rotateY: 0, opacity: 1 }}
                    exit={{ rotateY: 90, opacity: 0.8 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="w-full h-[480px]"
                    style={{ perspective: '1000px' }}
                  >
                    {/* Card Front - Pink Background Container */}
                    <div
                      className="w-full h-full rounded-xl p-4 flex relative shadow-lg"
                      style={{ background: activeCard.gradient }}
                    >
                      {/* Left Sidebar - Card Type */}
                      <div className="absolute left-0 top-[160px] w-[60px] h-full flex items-center justify-center">
                        <div 
                          className="font-bayon text-[30px] text-white drop-shadow-lg"
                          style={{ 
                            writingMode: 'vertical-lr',
                            transform: 'rotate(180deg)'
                          }}
                        >
                          {activeCard.type}
                        </div>
                      </div>

                      {/* White Content Area */}
                      <div className="flex-1 ml-[45px] bg-white rounded-lg p-5 flex flex-col items-center">
                        {/* Character Title */}
                        <div
                          className="font-bayon text-[32px] text-center mb-5"
                          style={{ color: activeCard.accentColor }}
                        >
                          {activeCard.title}
                        </div>
                        
                        {/* Character Image */}
                        <div className="w-[230px] h-[230px] rounded-xl overflow-hidden mb-5">
                          <img 
                            src={getCharacterImage(activeCardId)} 
                            alt={`Aki Kim - ${activeCard.type} version`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-full h-full bg-gray-400 flex items-center justify-center text-white font-bold text-2xl hidden">
                            AK
                          </div>
                        </div>
                        
                        {/* Power Section */}
                        <div className="text-center">
                          <div
                            className="font-bayon text-[25px] mb-1"
                            style={{ color: activeCard.accentColor }}
                          >
                            {activeCard.powerTitle}
                          </div>
                          <div className="space-y-1">
                            {activeCard.powers.map((power, index) => (
                              <div key={index} className="font-k2d text-lg text-[#333] leading-tight">
                                {power}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Card Selectors - now under the main card */}
                <div className="w-full">
                  <div className="grid grid-cols-4 gap-4">
                    {cards.map((card) => (
                      <button
                        key={card.id}
                        onClick={() => handleCardSwitch(card.id)}
                        disabled={isFlipping}
                        className={`w-full h-[120px] rounded-lg shadow-md transition-all duration-200 hover:transform hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-white/40 ${
                          activeCardId === card.id ? 'ring-2 ring-white/80 scale-105' : ''
                        }`}
                        style={{ background: card.gradient }}
                        aria-label={`View ${card.title} character card`}
                      >
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="font-bayon text-base text-white drop-shadow-sm transform -rotate-90">
                            {getSelectorText(card.id)}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section - Biography (9 columns on desktop) */}
            <div className="lg:col-span-7 lg:pl-20">
              <div className="flex items-center h-full">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="font-k2d text-[30px] leading-[35px] text-[#333]"
                >
                  Hi, I'm Aki, a product manager who loves solving messy problems at the crossroads of tech and people. I've shipped APIs that power connected cars, SaaS platforms that scale machine learning, and mobile features that make everyday experiences a little smoother. When I'm not roadmapping or running experiments, you'll probably find me training for an ultramarathon, tinkering with side projects, or getting lost at Costco.
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AboutPage;
